import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { createSearchParams, useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { apiUrl, gradeApi } from '../../../api.js';
import { useAuth } from '../../../auth/AuthContext';
import { useQueryParams } from '../../../shared/hooks/use-query-params.tsx';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearAssignmentStudents,
  getAssignmentStudents,
  getLogs,
  getSubmissions
} from '../../../store/teacherSlice.js';
import { DEFAULT_PAGE_NUMBER, ITEMS_PER_PAGE } from '../../../utils/pagination.ts';
import { XMarkIcon, TrashIcon, UserGroupIcon } from '@heroicons/react/24/outline';

// Import the new components
import SubmissionViewer from './SubmissionViewer';
import GradingRubric from './GradingRubric';
import StudentSidebar from './StudentSidebar';
import StudentSearchDropdown from '../TeacherAssignments/TeacherAssignmentSettings/StudentSearchDropdown';
import NavigationHeader from './NavigationHeader';

const NewTDetails = props => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { assignment_id, submission_id } = useParams();
  const navigate = useNavigate();

  // Get sort parameters from URL query string
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sortKey = queryParams.get('sortKey') || '';
  const sortDir = queryParams.get('sortDir') || 'asc';

  // Create a sorted version of submission IDs based on the sort parameters
  const [sortedSubmissionIds, setSortedSubmissionIds] = useState([]);
  // State
  const [submissionData, setSubmissionData] = useState({});
  const [preloadedPdfs, setPreloadedPdfs] = useState({});
  const [examPaper, setExamPaper] = useState([]);
  const [feedbackItems, setFeedbackItems] = useState({});
  const [editableFeedback, setEditableFeedback] = useState({});
  const [message, setMessage] = useState('');
  const [isEditingInstructorComment, setIsEditingInstructorComment] = useState({});
  const [editableInstructorComment, setEditableInstructorComment] = useState({});
  const [isEditingFeedback, setIsEditingFeedback] = useState({});
  const [assignment, setAssignment] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [isRegradeOpen, setIsRegradeOpen] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [regradeData, setRegradeData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmissionLoading, setIsSubmissionLoading] = useState(false);
  const [activeSubQuestionId, setActiveSubQuestionId] = useState(null);

  // Student sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [submissionsForSidebar, setSubmissionsForSidebar] = useState([]);
  const [isReviewed, setIsReviewed] = useState(false);
  const [assignedStudentId, setAssignedStudentId] = useState(null);
  const [error, setError] = useState(null);
  const [isStudentAssignmentConfirmationOpen, setIsStudentAssignmentConfirmationOpen] =
    useState(false);
  const [newStudentAssignment, setNewStudentAssignment] = useState(null);
  const [regradeInProgress, setRegradeInProgress] = useState({});
  const [regradeRequests, setRegradeRequests] = useState([]);
  const [nextRegrade, setNextRegrade] = useState(-1);
  // const [showSubmissionLog, setShowSubmissionLog] = useState(false);
  const regradeRef = useRef(null);
  const [prefetchedSubmissions, setPrefetchedSubmissions] = useState({});
  const [prefetchingSubmissionId, setPrefetchingSubmissionId] = useState(null);
  const [showNavTips, setShowNavTips] = useState(true);
  const [currentPdfPage, setCurrentPdfPage] = useState(() => {
    const savedPage = localStorage.getItem('currentPdfPage');
    return savedPage && !isNaN(parseInt(savedPage, 10)) ? parseInt(savedPage, 10) : 1;
  });
  const [pinnedSubQuestionId, setPinnedSubQuestionId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [pdfViewKey, setPdfViewKey] = useState(0);
  const [currentScale, setCurrentScale] = useState(1.0); // State for PDF zoom level
  const currentPageRef = useRef(1);

  // Refs
  const rightPanelRef = useRef(null);
  const instructorCommentRefs = useRef({});
  const pollingInterval = useRef(null);

  // Redux state
  const { data: students, isLoading: isAssignmentStudentsLoading } = useSelector(
    state => state?.teacherReducer?.assignmentStudents
  );
  const { data: logs, isLoading: isLogsLoading } = useSelector(
    state => state?.teacherReducer?.logs
  );

  // Query params
  const {
    params: { search, pageNumber: scrollPageNumber, itemsPerPage },
    updateSearchParams
  } = useQueryParams({
    search: '',
    pageNumber: DEFAULT_PAGE_NUMBER,
    itemsPerPage: ITEMS_PER_PAGE.default
  });

  // Initialize search params
  useEffect(() => {
    updateSearchParams({
      search: '',
      pageNumber: DEFAULT_PAGE_NUMBER
    });
  }, []);

  // Effect to load initial data
  useEffect(() => {
    let isMounted = true;

    if (assignment_id && submission_id && isMounted) {
      const loadData = async () => {
        try {
          await getQuestions();
          await getAssignmentSubmission();
          await getAssignment();
          if (isMounted) {
            startPolling();
          }
        } catch (error) {
          console.error('Error loading data:', error);
        }
      };

      loadData();
    }

    return () => {
      isMounted = false;
      stopPolling();
    };
  }, [assignment_id, submission_id]);

  // Effect to get regrades
  useEffect(() => {
    getRegrades();
  }, [props.regrade_question_id]);

  // Effect to get students when assignment changes
  useEffect(() => {
    if (assignment?.is_exam && assignment.course_id && submissionData.user) {
      getStudents();
    }
    return () => dispatch(clearAssignmentStudents());
  }, [assignment, submissionData, scrollPageNumber, itemsPerPage]);

  // Effect to handle regrade scrolling
  useEffect(() => {
    if (props.regrade_question_id && regradeRef.current) {
      const timer = setTimeout(() => {
        regradeRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [props.regrade_question_id, examPaper, regradeRef]);

  // Effect to get logs
  useEffect(() => {
    getAssignment();
    getStudents();
    getAssignmentSubmission();
  }, [submission_id]);

  // Effect to fetch all submissions using Redux Thunk for sidebar/navigation
  useEffect(() => {
    const fetchAllSubmissions = async () => {
      if (!assignment_id || !user) {
        return;
      }

      try {
        // Directly dispatch the thunk and await its result
        const actionResult = await dispatch(
          getSubmissions({
            assignmentId: assignment_id,
            token: user.session_token,
            search,
            pageNumber: 1,
            itemsPerPage: 9999,
            sortOrder: 'asc' // Get unsorted data from backend, we'll sort client-side
          })
        );

        // Check if the action was fulfilled
        if (actionResult?.type?.endsWith('/fulfilled')) {
          const payload = actionResult.payload;

          if (payload && payload.entities) {
            // Filter out entries with null id (not submitted)
            const submissionsWithId = payload.entities.filter(sub => sub.id !== null);

            // Create a copy of the entities to sort
            let sortedEntities = [...submissionsWithId];

            // Apply the same sorting logic as in TGrade.js
            // First sort based on the column header that was clicked
            if (sortKey) {
              sortedEntities.sort((a, b) => {
                // Get values to compare based on sortKey
                let aValue, bValue;

                switch (sortKey) {
                  case 'First Name':
                    aValue = a.first_name;
                    bValue = b.first_name;
                    break;
                  case 'Last Name':
                    aValue = a.last_name;
                    bValue = b.last_name;
                    break;
                  case 'Student':
                    // For backward compatibility with old URLs
                    aValue = a.full_name;
                    bValue = b.full_name;
                    break;
                  case 'Status':
                    aValue = a.status;
                    bValue = b.status;
                    break;
                  case 'Date Submitted':
                    // Handle null dates
                    aValue = a.submission_date ? new Date(a.submission_date).getTime() : null;
                    bValue = b.submission_date ? new Date(b.submission_date).getTime() : null;
                    break;
                  case 'Grade':
                    aValue = a.grade;
                    bValue = b.grade;
                    break;
                  default:
                    return 0;
                }

                // Handle null values
                if (aValue === null && bValue === null) {
                  return 0;
                }
                if (aValue === null) {
                  return 1; // Null values go at the end
                }
                if (bValue === null) {
                  return -1;
                }

                // Compare based on direction
                if (aValue < bValue) {
                  return sortDir === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                  return sortDir === 'asc' ? 1 : -1;
                }
                return 0;
              });
            }

            // Then apply priority sorting as a secondary sort (same as in TGrade.js)
            sortedEntities.sort((a, b) => {
              // First priority: Unmatched submissions always at the top
              if (
                a.first_name === 'Unmatched Submission' &&
                b.first_name !== 'Unmatched Submission'
              ) {
                return -1; // a goes before b
              }
              if (
                a.first_name !== 'Unmatched Submission' &&
                b.first_name === 'Unmatched Submission'
              ) {
                return 1; // a goes after b
              }

              // Second priority: Not submitted always at the bottom
              if (a.status === 'Not submitted' && b.status !== 'Not submitted') {
                return 1; // a goes after b
              }
              if (a.status !== 'Not submitted' && b.status === 'Not submitted') {
                return -1; // a goes before b
              }

              // Both are the same priority, keep original order from the previous sort
              return 0;
            });

            // Update state with sorted entities
            setSubmissionsForSidebar(sortedEntities);
            setSortedSubmissionIds(sortedEntities.map(sub => sub.id));
            setError(null);
          } else {
            console.warn('No results found when fetching all submissions.');
            setSubmissionsForSidebar([]);
            setSortedSubmissionIds([]);
          }
        } else {
          throw new Error('Action was not fulfilled');
        }
      } catch (err) {
        console.error('Error fetching all submissions via thunk:', err);
        setError(err?.message || 'Failed to load student list for sidebar.');
        setSubmissionsForSidebar([]);
        setSortedSubmissionIds([]);
      }
    };

    fetchAllSubmissions();
  }, [assignment_id, user, sortKey, sortDir, dispatch]); // Add dispatch to dependencies

  // Navigation functions for UI buttons
  const clickLeft = () => {
    // This is triggered by the "next" button in the UI
    navigateToNextSubmission();
  };

  const clickRight = () => {
    // This is triggered by the "previous" button in the UI
    navigateToPreviousSubmission();
  };

  // Helper function to cancel all currently editing feedback items
  const cancelAllEditingFeedback = () => {
    // Get all currently editing subquestion IDs
    const editingSubquestionIds = Object.keys(isEditingFeedback).filter(
      subquestionId => isEditingFeedback[subquestionId] === true
    );
    
    // Cancel editing for each one
    if (editingSubquestionIds.length > 0) {
      setIsEditingFeedback({});
    }
  };

  // Navigate to next subquestion when a subquestion is pinned
  const navigateToNextSubQuestion = useCallback(() => {
    if (!pinnedSubQuestionId || !examPaper || examPaper.length === 0) {
      return;
    }

    // Save the current page number to localStorage (only when in pinned mode)
    if (pinnedSubQuestionId && currentPageRef.current) {
      localStorage.setItem('currentPdfPage', currentPageRef.current.toString());
    }

    // Find all subquestions across all questions
    const allSubQuestions = examPaper.flatMap(question => question.subQuestions);
    if (allSubQuestions.length <= 1) {
      return;
    }

    // Find the index of the current pinned subquestion
    const currentIndex = allSubQuestions.findIndex(sq => sq.id === pinnedSubQuestionId);
    if (currentIndex === -1) {
      return;
    }

    // Get next index with wrap-around
    const nextIndex = (currentIndex + 1) % allSubQuestions.length;
    const nextSubQuestionId = allSubQuestions[nextIndex].id;

    // Pin the next subquestion
    setPinnedSubQuestionId(nextSubQuestionId);

    // Restore the saved page number for this pinned subquestion if it exists
    const savedPage = localStorage.getItem(`pinnedPage_${nextSubQuestionId}`);
    if (savedPage && !isNaN(parseInt(savedPage, 10))) {
      setCurrentPdfPage(parseInt(savedPage, 10));
    }

    // Scroll to the newly pinned subquestion with improved behavior
    setTimeout(() => {
      const element = document.getElementById(`subquestion-${nextSubQuestionId}`);
      if (element) {
        // Use 'center' alignment and smoother scrolling to make navigation more predictable
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center', // Position the element in the center of the viewport
          inline: 'nearest' // Don't scroll horizontally unless necessary
        });
      }
    }, 100);
  }, [pinnedSubQuestionId, examPaper, currentPageRef]);

  const navigateToPreviousSubQuestion = useCallback(() => {
    if (!pinnedSubQuestionId || !examPaper || examPaper.length === 0) {
      return;
    }

    // Find all subquestions across all questions
    const allSubQuestions = examPaper.flatMap(question => question.subQuestions);
    if (allSubQuestions.length <= 1) {
      return;
    }

    // Find the index of the current pinned subquestion
    const currentIndex = allSubQuestions.findIndex(sq => sq.id === pinnedSubQuestionId);
    if (currentIndex === -1) {
      return;
    }

    // Get previous index with wrap-around
    const prevIndex = (currentIndex - 1 + allSubQuestions.length) % allSubQuestions.length;
    const prevSubQuestionId = allSubQuestions[prevIndex].id;

    // Pin the previous subquestion
    setPinnedSubQuestionId(prevSubQuestionId);

    // Restore the saved page number for this pinned subquestion if it exists
    const savedPage = localStorage.getItem(`pinnedPage_${prevSubQuestionId}`);
    if (savedPage && !isNaN(parseInt(savedPage, 10))) {
      setCurrentPdfPage(parseInt(savedPage, 10));
    }

    // Scroll to the newly pinned subquestion with improved behavior
    setTimeout(() => {
      const element = document.getElementById(`subquestion-${prevSubQuestionId}`);
      if (element) {
        // Use 'center' alignment and smoother scrolling to make navigation more predictable
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center', // Position the element in the center of the viewport
          inline: 'nearest' // Don't scroll horizontally unless necessary
        });
      }
    }, 100);
  }, [pinnedSubQuestionId, examPaper]);

  // Separate useEffect for keyboard navigation
  useEffect(() => {
    const handleKeyDown = event => {
      // Prevent navigation if focus is on an input/textarea
      if (['input', 'textarea'].includes(event.target.tagName.toLowerCase())) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        clickRight(); // Previous student
      } else if (event.key === 'ArrowRight') {
        clickLeft(); // Next student
      } else if (pinnedSubQuestionId !== null) {
        // When a subquestion is pinned, Up/Down arrows navigate between pinned subquestions
        // Prevent default browser scrolling for arrow keys when in pinned navigation mode
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault();

          if (event.key === 'ArrowUp') {
            navigateToPreviousSubQuestion();
          } else {
            navigateToNextSubQuestion();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    submission_id,
    clickRight,
    clickLeft,
    pinnedSubQuestionId,
    setCurrentPdfPage,
    setPdfViewKey,
    navigateToPreviousSubQuestion,
    navigateToNextSubQuestion
  ]);

  // Track the "next next" PDF filename to preload
  const [preloadNextNextPdf, setPreloadNextNextPdf] = useState(null);

  // ...

  const navigateToPreviousSubmission = () => {
    cancelAllEditingFeedback();
    // Use sorted IDs if available, otherwise fall back to props.submissionIds
    const submissionIdsToUse =
      sortedSubmissionIds.length > 0 ? sortedSubmissionIds : props.submissionIds;

    if (!submissionIdsToUse || submissionIdsToUse.length === 0) {
      return;
    }
    const currentIndex = submissionIdsToUse.indexOf(Number(submission_id));
    const prevIndex =
      currentIndex < 0
        ? submissionIdsToUse.length - 1
        : (currentIndex - 1 + submissionIdsToUse.length) % submissionIdsToUse.length;

    navigateToSubmission(prevIndex, -1, false, null, 'right');
  };

  const navigateToNextSubmission = () => {
    cancelAllEditingFeedback();
    // Use sorted IDs if available, otherwise fall back to props.submissionIds
    const submissionIdsToUse =
      sortedSubmissionIds.length > 0 ? sortedSubmissionIds : props.submissionIds;

    if (!submissionIdsToUse || submissionIdsToUse.length === 0) {
      return;
    }

    // If all criteria are met for marking a submission as reviewed
    if (
      assignment?.rubric_settings?.auto_review_with_navigation &&
      submissionData?.grade_status !== 'graded'
    ) {
      handleMarkReviewed(true);
    }

    const currentIndex = submissionIdsToUse.indexOf(Number(submission_id));
    if (currentIndex < 0) {
      return;
    }

    const nextIndex = (currentIndex + 1) % submissionIdsToUse.length;
    const nextSubmissionId = submissionIdsToUse[nextIndex];

    if (!nextSubmissionId) {
      return;
    }

    // Preload the PDF that comes after the next one ("next next")
    console.log('Navigation successful, now preloading next next PDF');
    try {
      // Find the submission after the next one
      const nextNextIndex = (nextIndex + 1) % submissionIdsToUse.length;
      const nextNextSubmissionId = submissionIdsToUse[nextNextIndex];

      if (nextNextSubmissionId) {
        console.log('Will fetch next next submission data:', nextNextSubmissionId);

        // Use gradeApi which properly handles authentication
        gradeApi
          .get({
            path: `/api/assignment/${assignment_id}/submission/${nextNextSubmissionId}/`,
            config: {
              headers: {
                Authorization: `Bearer ${user.session_token}`
              }
            }
          })
          .then(response => {
            const data = response.data;
            if (data?.file?.filename) {
              console.log('Got filename for next next PDF:', data.file.filename);
              setPreloadNextNextPdf(data.file.filename); // Set the filename to preload
            } else {
              console.warn('No filename in response for submission:', nextNextSubmissionId);
            }
          })
          .catch(error => {
            console.error('Error fetching next next submission:', error.response?.status || error);
          });
      } else {
        console.log('No next next submission ID available');
      }
    } catch (error) {
      console.error('Error in PDF preloading logic:', error);
    }

    localStorage.setItem('currentPdfPage', String(currentPdfPage));
    localStorage.setItem('lastSubmissionId', submission_id.toString());

    const searchTerm = typeof search !== 'undefined' ? search : '';
    const pageNum =
      typeof scrollPageNumber !== 'undefined' ? scrollPageNumber : DEFAULT_PAGE_NUMBER;
    const itemsCount = typeof itemsPerPage !== 'undefined' ? itemsPerPage : ITEMS_PER_PAGE.default;
    const currentSortKey = sortKey || '';
    const currentSortDir = sortDir || 'asc';

    const pathname = `/tclasses/${props.class_id}/tassignments/${assignment_id}/tsubmissions/${nextSubmissionId}/tdetails`;
    navigate(
      {
        pathname,
        search: `?${createSearchParams({
          search: searchTerm,
          pageNumber: pageNum,
          itemsPerPage: itemsCount,
          sortKey: currentSortKey,
          sortDir: currentSortDir
        })}`
      },
      { replace: true }
    );
    if (!pinnedSubQuestionId && rightPanelRef.current) {
      setTimeout(() => {
        if (rightPanelRef.current) {
          // Re-check ref inside timeout
          rightPanelRef.current.scrollTop = 0;
        }
      }, 0);
    }
  };

  const navigateToSubmission = (
    startIndex,
    direction,
    isUnseenOnly = false,
    availableIds,
    animationDirection = null
  ) => {
    cancelAllEditingFeedback();
    const searchTerm = typeof search !== 'undefined' ? search : '';
    const pageNum =
      typeof scrollPageNumber !== 'undefined' ? scrollPageNumber : DEFAULT_PAGE_NUMBER;
    const itemsCount = typeof itemsPerPage !== 'undefined' ? itemsPerPage : ITEMS_PER_PAGE.default;

    // Add sort parameters to preserve the table's sort order
    const currentSortKey = sortKey || '';
    const currentSortDir = sortDir || 'asc';

    // Use sorted IDs if available, otherwise fall back to props.submissionIds
    const submissionIdsToUse =
      sortedSubmissionIds.length > 0 ? sortedSubmissionIds : props.submissionIds;

    if (submissionIdsToUse.length === 1) {
      return;
    }

    setEditableFeedback({});

    let currentIndex = startIndex;
    const ids = isUnseenOnly ? availableIds : submissionIdsToUse;
    const maxAttempts = ids.length;

    if (currentIndex === null || maxAttempts === 0) {
      navigate({
        pathname: `/tclasses/${props.class_id}/tassignments/${assignment_id}/tsubmissions`,
        search: `?${createSearchParams({
          search: searchTerm,
          pageNumber: pageNum,
          itemsPerPage: itemsCount
        })}`
      });
      return;
    }

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const nextSubmissionId = ids[currentIndex];
      if (nextSubmissionId !== null) {
        if (props.regrade_question_id) {
          for (const request of regradeRequests) {
            if (request.id === nextSubmissionId) {
              let subquestion_id = null;
              for (const subquestion in request.feedback) {
                if (request.feedback[subquestion].regrade_request) {
                  subquestion_id = subquestion;
                  break;
                }
              }

              const pathname = `/tclasses/${request.assignment.course.id}/tassignments/${request.assignment.id}/tsubmissions/${nextSubmissionId}/tdetails/${subquestion_id}`;
              navigate(
                {
                  pathname,
                  search: `?${createSearchParams({
                    search: searchTerm,
                    pageNumber: pageNum,
                    itemsPerPage: itemsCount,
                    sortKey: currentSortKey,
                    sortDir: currentSortDir
                  })}`
                },
                { replace: true }
              );
              if (!pinnedSubQuestionId && rightPanelRef.current) {
                setTimeout(() => {
                  if (rightPanelRef.current) {
                    // Re-check ref inside timeout
                    rightPanelRef.current.scrollTop = 0;
                  }
                }, 0);
              }
              return;
            }
          }
        } else {
          const pathname = `/tclasses/${props.class_id}/tassignments/${assignment_id}/tsubmissions/${nextSubmissionId}/tdetails`;
          navigate(
            {
              pathname,
              search: `?${createSearchParams({
                search: searchTerm,
                pageNumber: pageNum,
                itemsPerPage: itemsCount,
                sortKey: currentSortKey,
                sortDir: currentSortDir
              })}`
            },
            { replace: true }
          );
          if (!pinnedSubQuestionId && rightPanelRef.current) {
            setTimeout(() => {
              if (rightPanelRef.current) {
                // Re-check ref inside timeout
                rightPanelRef.current.scrollTop = 0;
              }
            }, 0);
          }
          return;
        }
      }

      // Move to the next index
      currentIndex = (currentIndex + direction + ids.length) % ids.length;
    }
  };

  // Helper to extract all subquestions from submission data
  const getAllSubQuestions = submission => {
    if (!submission || !submission.exam_result || !submission.exam_result.questions) {
      return [];
    }
    return submission.exam_result.questions.flatMap(q => q.subquestions || []);
  };

  // Handle student assignment
  const handleConfirmStudentAssignment = async () => {
    try {
      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/submissions/${submission_id}/assign-student/`,
        body: { new_student_id: newStudentAssignment.id },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      setAssignedStudentId(newStudentAssignment.id);
      setStudentName(newStudentAssignment.full_name);
      getAssignmentSubmission();
      setIsStudentAssignmentConfirmationOpen(false);
    } catch (error) {
      console.error('Failed to assign student:', error);
      setError('Failed to assign student. Please try again.');
    }
  };

  // Handle delete submission
  const handleDeleteSubmission = () => {
    const studentId = assignedStudentId || submissionData?.user?.id;
    if (!studentId) {
      setError('Cannot delete: No student ID found for this submission');
      return;
    }
    setStudentToDelete(studentId);
    setShowDeleteModal(true);
  };

  const confirmDeleteSubmission = async () => {
    try {
      if (!studentToDelete) {
        return;
      }

      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/delete-student-submissions/`,
        body: {
          student_id: studentToDelete
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      setShowDeleteModal(false);
      setStudentToDelete(null);
      setError('');
      clickLeft();
    } catch (error) {
      console.error('Error deleting student submissions:', error);
      setError('Failed to delete student submissions. Please try again.');
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setStudentToDelete(null);
  };

  // Mark as reviewed
  const handleMarkReviewed = async () => {
    try {
      const newReviewedStatus = !isReviewed;
      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/submissions/${submission_id}/mark-reviewed/`,
        body: { is_reviewed: newReviewedStatus },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      setIsReviewed(newReviewedStatus);
    } catch (error) {
      console.error('Failed to update review status:', error);
      setMessage('Failed to update review status.');
    }
  };

  // Handle rubric grading - now sends direct score values with optimistic UI updates
  const handleRubricItemChange = (subquestionId, scoreOrRubricItem) => {
    return () => {
      // Determine if we're receiving a score value or a traditional rubric item
      const isScoreValue = typeof scoreOrRubricItem === 'number';
      const requestBody = {
        subquestionId: subquestionId
      };

      // If it's a direct score, use that; otherwise use the traditional rubric item
      if (isScoreValue) {
        requestBody.score = scoreOrRubricItem;
      } else {
        requestBody.rubric = scoreOrRubricItem;
      }

      // Immediately update UI for better responsiveness (optimistic update)
      setFeedbackItems(prevFeedbackItems => {
        const newFeedbackItems = { ...prevFeedbackItems };

        // If we don't have feedback for this subquestion yet, create it
        if (!newFeedbackItems[subquestionId]) {
          newFeedbackItems[subquestionId] = {};
        }

        // Update with the new score or rubric item
        if (isScoreValue) {
          newFeedbackItems[subquestionId].score = scoreOrRubricItem;
        } else if (scoreOrRubricItem?.id) {
          newFeedbackItems[subquestionId].rubric_items = [scoreOrRubricItem.id];
        }

        return newFeedbackItems;
      });

      // Also update the submission grade if we can estimate it
      if (isScoreValue) {
        // This is a simplified calculation - the actual grade might be calculated differently
        // on the backend, but this gives immediate feedback
        const estimatedGrade = calculateEstimatedGrade(scoreOrRubricItem, subquestionId);
        if (estimatedGrade !== null) {
          setSubmissionData({ ...submissionData, grade: estimatedGrade });
        }
      }

      // Then send the API request
      gradeApi
        .post({
          path: `/api/assignment/${assignment_id}/submissions/${submission_id}/grade/`,
          body: requestBody,
          config: {
            headers: {
              Authorization: `Bearer ${user.session_token}`
            }
          }
        })
        .catch(error => {
          console.error('Error updating grade:', error);
          // Revert optimistic updates on error
          // This would require storing previous state, omitted for brevity
        })
        .then(response => {
          if (!response) {
            return;
          }

          // Still update with server response once we have it (for accuracy)
          setFeedbackItems(prevFeedbackItems => {
            const newFeedbackItems = { ...prevFeedbackItems };
            Object.keys(response.data.feedback).forEach(key => {
              newFeedbackItems[key] = {
                ...prevFeedbackItems[key],
                ...response.data.feedback[key],
                instructor_text:
                  prevFeedbackItems[key]?.instructor_text ||
                  response.data.feedback[key]?.instructor_text
              };
            });
            return newFeedbackItems;
          });

          setSubmissionData({ ...submissionData, grade: response.data.grade });
        });
    };
  };

  // Helper function to estimate the grade when updating a score
  // This provides immediate UI feedback before backend responds
  const calculateEstimatedGrade = (newScore, changedSubquestionId) => {
    try {
      // Get all current feedback items
      const allFeedback = { ...feedbackItems };

      // Update the changed subquestion with the new score
      if (!allFeedback[changedSubquestionId]) {
        allFeedback[changedSubquestionId] = {};
      }
      allFeedback[changedSubquestionId].score = newScore;

      // Sum up all scores across subquestions
      let totalScore = 0;
      let totalMaxScore = 0;

      // Go through exam paper structure to accumulate scores
      if (examPaper && examPaper.length > 0) {
        for (const question of examPaper) {
          if (question.subquestions && question.subquestions.length > 0) {
            for (const sq of question.subquestions) {
              const feedback = allFeedback[sq.id];
              const score = feedback?.score || 0;
              totalScore += parseFloat(score);
              totalMaxScore += parseFloat(sq.max_weight || 0);
            }
          }
        }
      }

      // Calculate percentage score
      if (totalMaxScore > 0) {
        return Math.round((totalScore / totalMaxScore) * 100);
      }
      return null;
    } catch (error) {
      console.error('Error calculating estimated grade:', error);
      return null;
    }
  };

  // Handle regrade requests
  const handleRegradeReq = subquestionId => {
    if (subquestionId in feedbackItems) {
      handleRegradeReqDel(subquestionId);
    }
  };

  const handleRegradeReqDel = subquestionId => {
    gradeApi
      .post({
        path: `/api/assignment/${assignment_id}/submissions/${submission_id}/regrade-request/`,
        body: {
          subquestionId: subquestionId,
          regradeRequest: false
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      })
      .then(response => {
        if (response) {
          setFeedbackItems(prevFeedbackItems => ({
            ...prevFeedbackItems,
            [subquestionId]: {
              ...prevFeedbackItems[subquestionId],
              regrade_request: false
            }
          }));
        }
      })
      .catch(error => {
        console.error('Error updating regrade request:', error);
      });
  };

  // Handle regrade
  const handleRegradeClick = subQuestionId => {
    setActiveSubQuestionId(subQuestionId);
    localStorage.setItem('activeSubQuestionId', subQuestionId);
    setIsRegradeOpen(true);
  };

  const handleRegradeClose = () => {
    setIsRegradeOpen(false);
    setActiveSubQuestionId(null);
  };

  const handleRegradeSubmit = (gradingCriteria, solution, maxWeight) => {
    setRegradeData({ gradingCriteria, solution, maxWeight });
    setIsConfirmationOpen(true);
  };

  const handleConfirmRegrade = async () => {
    setIsConfirmationOpen(false);
    setIsLoading(true);
    try {
      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/ai-regrade/`,
        body: {
          subQuestionId: activeSubQuestionId,
          gradingCriteria: regradeData.gradingCriteria,
          solution: regradeData.solution,
          maxWeight: regradeData.maxWeight
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      getQuestions();
      getAssignmentSubmission();
      setMessage('AI-Regrade initiated. The system will update the grades shortly.');
    } catch (error) {
      console.error('AI-Regrade failed:', error);
      setMessage('AI-Regrade failed. Please try again.');
    } finally {
      setIsLoading(false);
      setIsRegradeOpen(false);
      setActiveSubQuestionId(null);
    }
  };

  // Handle feedback
  const handleEditFeedback = subquestionId => {
    if (!feedbackItems) {
      return;
    }

    // Update the editable feedback with the current feedback content
    setEditableFeedback(prev => {
      const newState = {
        ...prev,
        [subquestionId]: feedbackItems[subquestionId]?.feedback || ''
      };
      // console.log('New editable feedback state:', newState);
      return newState;
    });

    // Mark this subquestion as being edited
    setIsEditingFeedback(prev => {
      const newState = {
        ...prev,
        [subquestionId]: true
      };
      // console.log('New editing state:', newState);
      return newState;
    });
  };

  const handleFeedbackChange = (subquestionId, value) => {
    setEditableFeedback(prev => ({
      ...prev,
      [subquestionId]: value
    }));
  };

  const handleFeedbackSave = (subquestionId, feedbackContent) => {
    if (!feedbackItems) {
      return;
    }

    const updatedFeedback =
      feedbackContent || (editableFeedback && editableFeedback[subquestionId]);

    // Immediately update the UI first for instant feedback
    const newFeedbackItems = { ...feedbackItems };
    if (!newFeedbackItems[subquestionId]) {
      newFeedbackItems[subquestionId] = {};
    }

    // Update both feedback and instructor_text to ensure compatibility
    newFeedbackItems[subquestionId] = {
      ...newFeedbackItems[subquestionId],
      feedback: updatedFeedback,
      instructor_text: updatedFeedback
    };

    // Set the new state directly to immediately update the UI
    setFeedbackItems(newFeedbackItems);

    // Reset the editing state immediately
    setIsEditingFeedback(prev => {
      const newState = { ...prev };
      delete newState[subquestionId];
      return newState;
    });

    // Show quick feedback message
    setMessage('Feedback saved');
    setTimeout(() => setMessage(''), 3000);

    // Then make the API call to persist the change
    gradeApi
      .post({
        path: `api/assignment/${assignment_id}/submissions/${submission_id}/instructor-feedback/`,
        body: {
          subquestionId: subquestionId,
          instructorText: updatedFeedback
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      })
      .then(response => {
        // No need to do anything, the UI is already updated
      })
      .catch(error => {
        console.error('Error saving feedback:', error);
        setMessage('Error saving feedback');
        setTimeout(() => setMessage(''), 3000);

        // Revert to server state if the API call fails
        getAssignmentSubmission();
      });
  };

  const handleFeedbackCancel = subquestionId => {
    setIsEditingFeedback(prev => {
      const newState = { ...prev };
      delete newState[subquestionId];
      // console.log('After cancel, editing state:', newState);
      return newState;
    });
  };

  const handleRemoveFeedback = subquestionId => {
    // Immediately update the UI first for instant feedback
    const newFeedbackItems = { ...feedbackItems };
    if (newFeedbackItems[subquestionId]) {
      newFeedbackItems[subquestionId] = {
        ...newFeedbackItems[subquestionId],
        feedback: '',
        instructor_text: ''
      };
    }
    // Set the new state directly to immediately update the UI
    setFeedbackItems(newFeedbackItems);

    // Then make the API call to persist the change
    gradeApi
      .post({
        path: `api/assignment/${assignment_id}/submissions/${submission_id}/instructor-feedback/`,
        body: {
          subquestionId: subquestionId,
          instructorText: ''
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      })
      .then(response => {
        // Only show message on success, don't reload everything
        setMessage('Feedback removed');
        setTimeout(() => setMessage(''), 3000);
      })
      .catch(error => {
        console.error('Error removing feedback:', error);
        setMessage('Error removing feedback');
        setTimeout(() => setMessage(''), 3000);

        // Revert the local state change if the API call fails
        getAssignmentSubmission();
      });
  };

  // Handle instructor comments
  const handleAddInstructorFeedback = subquestionId => {
    const currentFeedback = feedbackItems[subquestionId]?.instructor_text || '';
    setEditableInstructorComment(prev => ({
      ...prev,
      [subquestionId]: currentFeedback
    }));

    setIsEditingInstructorComment(prev => ({
      ...prev,
      [subquestionId]: true
    }));

    setTimeout(() => {
      if (instructorCommentRefs.current[subquestionId]) {
        instructorCommentRefs.current[subquestionId].focus();
      }
    }, 0);
  };

  const handleInstructorCommentChange = (subquestionId, value) => {
    setEditableInstructorComment(prev => ({
      ...prev,
      [subquestionId]: value
    }));
  };

  const fetchSubmission = async submissionId => {
    if (!submissionId || !assignment_id) {
      return null;
    }

    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment_id}/submission/${submissionId}/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching submission:', error);
      return null;
    }
  };

  // We no longer need this function as we're using the PdfPreloader component
  const preloadPdf = useCallback(
    filename => {
      if (!filename || preloadedPdfs[filename]) {
        return;
      }

      console.log('Marking PDF as preloaded:', filename);

      // Just mark as preloaded to prevent duplicate requests
      setPreloadedPdfs(prev => ({
        ...prev,
        [filename]: true
      }));
    },
    [preloadedPdfs]
  );

  // Pre-fetch submissions for smoother navigation
  const prefetchSubmission = useCallback(
    async submissionId => {
      if (
        !submissionId ||
        prefetchingSubmissionId === submissionId ||
        prefetchedSubmissions[submissionId]
      ) {
        return;
      }

      setPrefetchingSubmissionId(submissionId);

      try {
        const data = await fetchSubmission(submissionId);
        if (data) {
          setPrefetchedSubmissions(prev => ({
            ...prev,
            [submissionId]: data
          }));

          // If this submission has a PDF file, preload it
          if (data.file && data.file.filename) {
            preloadPdf(data.file.filename);
          }
        }
      } finally {
        setPrefetchingSubmissionId(null);
      }
    },
    [assignment_id, user.session_token, prefetchingSubmissionId, prefetchedSubmissions, preloadPdf]
  );

  // API calls
  const getRegrades = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/regrade-requests/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      const regradesForTeacher = response.data;
      setRegradeRequests(regradesForTeacher);
      const currentIndex = regradesForTeacher.map(item => item.id).indexOf(Number(submission_id));
      const feedback = regradesForTeacher[currentIndex]?.feedback;

      let nextFeedback = -1;
      let found = false;
      for (const feedbackItem in feedback) {
        if (found && feedback[feedbackItem].regrade_request) {
          nextFeedback = feedbackItem;
          setNextRegrade(nextFeedback);
          break;
        }
        if (feedbackItem == props.regrade_question_id) {
          found = true;
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getAssignmentSubmission = async () => {
    setIsSubmissionLoading(true);

    try {
      if (prefetchedSubmissions[submission_id]) {
        const data = prefetchedSubmissions[submission_id];
        setSubmissionData(data);
        setFeedbackItems(data?.submitted ? data?.feedback : {});
        setIsReviewed(data.is_reviewed);
        setAssignedStudentId(data.user.id);
        setStudentName(`${data.user.first_name} ${data.user.last_name}`);

        setPrefetchedSubmissions(prev => {
          const { [submission_id]: _, ...rest } = prev;
          return rest;
        });
      } else {
        const response = await fetchSubmission(submission_id);
        if (response) {
          setSubmissionData(response);
          setFeedbackItems(response?.submitted ? response?.feedback : {});
          setIsReviewed(response.is_reviewed);
          setAssignedStudentId(response.user.id);
          setStudentName(`${response.user.first_name} ${response.user.last_name}`);
        }
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Error fetching submission');
    } finally {
      setIsSubmissionLoading(false);
    }
  };

  const getQuestions = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment_id}/questions/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      const questionsFromDB = response.data;

      if (pollingInterval.current) {
        setExamPaper(prevExamPaper =>
          prevExamPaper.map(question => ({
            ...question,
            subQuestions: question.subQuestions.map(subQuestion => {
              const updatedSubQuestion = questionsFromDB.questions
                .flatMap(q => q.subQuestions)
                .find(sq => sq.id === subQuestion.id);

              if (regradeInProgress[subQuestion.id]) {
                return updatedSubQuestion || subQuestion;
              }
              return subQuestion;
            })
          }))
        );
      } else {
        setExamPaper(questionsFromDB.questions);
      }

      const newRegradeStatus = {};
      questionsFromDB.questions.forEach(question => {
        question.subQuestions.forEach(subQuestion => {
          if (subQuestion.regrade_in_progress) {
            newRegradeStatus[subQuestion.id] = true;
          }
        });
      });
      setRegradeInProgress(newRegradeStatus);

      if (Object.values(newRegradeStatus).some(status => status)) {
        startPolling();
      } else {
        stopPolling();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getAssignment = async () => {
    try {
      const response = await gradeApi.get({
        path: `/api/assignment/${assignment_id}/only/`,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });
      setAssignment(response.data);
    } catch (error) {
      setMessage('Failed to fetch assignment details');
    }
  };

  const getStudents = async () => {
    // Make sure assignment is loaded before accessing course_id
    if (!assignment) {
      // console.log('Assignment not loaded yet, cannot get students');
      return;
    }

    await dispatch(
      getAssignmentStudents({
        classId: assignment.course_id,
        token: user.session_token,
        search,
        pageNumber: Number(scrollPageNumber),
        itemsPerPage: Number(itemsPerPage)
      })
    );

    // Make sure submissionData is loaded before accessing user.id
    if (submissionData && submissionData.user) {
      setAssignedStudentId(submissionData.user.id);
    }
  };

  const getAllLogs = async () => {
    await dispatch(
      getLogs({
        userId: assignedStudentId,
        token: user.session_token,
        assignmentId: assignment_id
      })
    );
  };

  // Polling functions
  const startPolling = () => {
    if (!pollingInterval.current) {
      pollingInterval.current = setInterval(getQuestions, 5000);
    }
  };

  const stopPolling = () => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  };

  const handleInstructorCommentSave = subquestionId => {
    gradeApi
      .post({
        path: `api/assignment/${assignment_id}/submissions/${submission_id}/instructor-feedback/`,
        body: {
          subquestionId: subquestionId,
          instructorText: editableInstructorComment[subquestionId]
        },
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      })
      .then(response => {
        if (response) {
          setFeedbackItems(prevFeedbackItems => ({
            ...prevFeedbackItems,
            [subquestionId]: {
              ...prevFeedbackItems[subquestionId],
              instructor_text: editableInstructorComment[subquestionId]
            }
          }));

          setIsEditingInstructorComment(prev => {
            const { [subquestionId]: _, ...rest } = prev;
            return rest;
          });

          setEditableInstructorComment(prev => {
            const { [subquestionId]: _, ...rest } = prev;
            return rest;
          });
        }
      })
      .catch(error => {
        console.error('Error updating instructor comment:', error);
      });
  };

  const handleInstructorCommentCancel = subquestionId => {
    setIsEditingInstructorComment(prev => {
      const { [subquestionId]: _, ...rest } = prev;
      return rest;
    });

    setEditableInstructorComment(prev => {
      const { [subquestionId]: _, ...rest } = prev;
      return rest;
    });
  };

  // Toggle subquestion pinning
  const togglePinSubQuestion = useCallback(
    subQuestionId => {
      console.log('Toggle pin - Current page:', currentPageRef.current);
      console.log(
        'Already saved page for this subq:',
        localStorage.getItem(`pinnedPage_${subQuestionId}`)
      );

      if (pinnedSubQuestionId === subQuestionId) {
        // Unpinning - reset to page 1
        setPinnedSubQuestionId(null);
        setCurrentPdfPage(1); // Reset to page 1 when unpinning
        currentPageRef.current = 1;
        console.log('Unpinned - reset to page 1');
      } else {
        // Save the current page first if we have an existing pinned subquestion
        if (pinnedSubQuestionId) {
          localStorage.setItem(`pinnedPage_${pinnedSubQuestionId}`, String(currentPageRef.current));
          console.log(
            'Saved current page for previous pinned subq:',
            pinnedSubQuestionId,
            currentPageRef.current
          );
        }

        // Now set the new pinned subquestion
        setPinnedSubQuestionId(subQuestionId);

        // First time pinning this subquestion - use the current page we're viewing
        const existingSavedPage = localStorage.getItem(`pinnedPage_${subQuestionId}`);
        console.log('Existing saved page:', existingSavedPage);

        if (existingSavedPage && !isNaN(parseInt(existingSavedPage, 10))) {
          console.log('Using saved page:', parseInt(existingSavedPage, 10));
          setCurrentPdfPage(parseInt(existingSavedPage, 10));
          currentPageRef.current = parseInt(existingSavedPage, 10);
        } else {
          // No saved page, use the current page (we're already looking at the right page)
          console.log('No saved page, keeping current page:', currentPageRef.current);
          localStorage.setItem(`pinnedPage_${subQuestionId}`, String(currentPageRef.current));
          // No need to set currentPdfPage as we're staying on the same page
        }
      }
    },
    [pinnedSubQuestionId]
  );

  // Initialize localStorage for page numbers if needed
  useEffect(() => {
    // On component mount, clear any stale saved pages
    const storageKeys = Object.keys(localStorage);
    for (const key of storageKeys) {
      if (key.startsWith('pinnedPage_')) {
        localStorage.removeItem(key);
      }
    }
  }, []);

  // Helper function to get question identifier (e.g., '1a') from subquestion ID
  const getQuestionIdentifier = subQuestionId => {
    if (!examPaper || !subQuestionId) {
      return '';
    }

    for (let questionIndex = 0; questionIndex < examPaper.length; questionIndex++) {
      const question = examPaper[questionIndex];
      for (
        let subQuestionIndex = 0;
        subQuestionIndex < question.subQuestions.length;
        subQuestionIndex++
      ) {
        if (question.subQuestions[subQuestionIndex].id === subQuestionId) {
          return `${questionIndex + 1}${String.fromCharCode(97 + subQuestionIndex)}`;
        }
      }
    }
    return '';
  };

  // Helper function to check if a rubric item is selected

  const hasRubricItem = (subquestionId, rubricId) => {
    if (!feedbackItems || !subquestionId) {
      return false;
    }
    if (subquestionId in feedbackItems) {
      let rubricItems = feedbackItems[subquestionId]?.rubric_items;
      for (let i = 0; i < rubricItems?.length; i++) {
        if (rubricItems[i].id === rubricId) {
          return true;
        }
      }
    }
    return false;
  };
  // Get feedback OBJECT for a subquestion
  const getFeedback = subquestionId => {
    if (!feedbackItems) {
      return null; // Return null if no feedback items exist
    }
    if (subquestionId in feedbackItems) {
      // Return the entire feedback object for this subquestion
      return feedbackItems[subquestionId];
    }
    // Return null if feedback for this specific subquestion isn't found
    return null;
  };

  // Get instructor feedback for a subquestion
  const getInstructorFeedback = subquestionId => {
    if (!feedbackItems) {
      return '';
    }
    if (subquestionId in feedbackItems) {
      return feedbackItems[subquestionId].instructor_text;
    }
    return '';
  };

  // Get regrade request for a subquestion
  const getRR = subquestionId => {
    if (!feedbackItems) {
      return '';
    }
    if (subquestionId in feedbackItems) {
      return feedbackItems[subquestionId].regrade_request;
    }
    return '';
  };

  // Get regrade request text for a subquestion
  const getRRText = subquestionId => {
    if (!feedbackItems) {
      return '';
    }
    if (subquestionId in feedbackItems) {
      return feedbackItems[subquestionId].regrade_text;
    }
    return '';
  };

  // Check if in editing mode
  const isEditing = subquestionId => editableFeedback.hasOwnProperty(subquestionId);
  const isEditingInstructor = subquestionId =>
    isEditingInstructorComment.hasOwnProperty(subquestionId);
  const isEditingFeedbackCheck = subquestionId => {
    if (!subquestionId) {
      return false;
    }
    const result = isEditingFeedback && isEditingFeedback[subquestionId] === true;
    return result;
  };

  // Get filenames for PDFs that should be preloaded

  // Get filenames for PDFs that should be preloaded
  const pdfFilesToPreload = useMemo(() => {
    const files = [];
    // Add PDFs from prefetched submissions that aren't already preloaded
    Object.values(prefetchedSubmissions).forEach(submission => {
      if (submission?.file?.filename && !preloadedPdfs[submission.file.filename]) {
        files.push(submission.file.filename);
      }
    });
    return files;
  }, [prefetchedSubmissions, preloadedPdfs]);

  const handleScaleChange = useCallback(newScale => {
    // Optional: Add constraints or debouncing if needed
    setCurrentScale(newScale);
  }, []);

  useEffect(() => {
    if (pinnedSubQuestionId) {
      // When a subquestion is pinned, restore its saved page number
      const savedPage = localStorage.getItem(`pinnedPage_${pinnedSubQuestionId}`);
      if (savedPage && !isNaN(parseInt(savedPage, 10))) {
        setCurrentPdfPage(parseInt(savedPage, 10));
        // Force PDF view to re-render with the new page
        setPdfViewKey(prevKey => prevKey + 1);
      }
    }
  }, [pinnedSubQuestionId]);

  return (
    <div className="flex flex-col h-screen w-full" style={{ background: 'rgb(251, 251, 251)' }}>
      {/* Invisible PDF Preloader for batch preloading */}
      <PdfPreloader pdfFiles={pdfFilesToPreload} apiUrl={apiUrl} />

      {/* Single file preloader for "next next" PDF */}
      {preloadNextNextPdf && <PdfPreloader singleFile={preloadNextNextPdf} apiUrl={apiUrl} />}

      {/* Header */}
      <NavigationHeader
        assignment={assignment}
        studentName={studentName}
        submission_id={submission_id}
        class_id={props.class_id}
        assignment_id={assignment_id}
        submissionIds={sortedSubmissionIds.length > 0 ? sortedSubmissionIds : props.submissionIds}
        clickLeft={clickLeft}
        clickRight={clickRight}
        isReviewed={isReviewed}
        handleMarkReviewed={handleMarkReviewed}
        pinnedSubQuestionId={pinnedSubQuestionId}
        navigateToPreviousSubQuestion={navigateToPreviousSubQuestion}
        navigateToNextSubQuestion={navigateToNextSubQuestion}
        submissionData={submissionData}
      />

      {/* Student sidebar toggle button */}
      <button
        className="fixed left-4 top-1/2 transform -translate-y-1/2 z-40 flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 group"
        aria-label="Toggle student list"
        title="View all students"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        <div className="relative">
          <UserGroupIcon className="w-6 h-6 text-amber-600 group-hover:scale-110 transition-transform duration-300" />
        </div>
      </button>

      {/* Student sidebar */}
      <StudentSidebar
        isOpen={isSidebarOpen}
        students={submissionsForSidebar}
        currentSubmissionId={submission_id}
        assignment={assignment}
        sortKey={sortKey}
        sortDir={sortDir}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Main content */}
      <div className="flex fixed left-8 right-0 bottom-0 top-0 h-[calc(100vh-4.5rem)] mt-[4.5rem] overflow-hidden">
        {/* Student submission view */}
        <div
          className="w-1/2 overflow-hidden"
          style={{ height: '100%', width: '50%', flexBasis: '50%' }}
        >
          {submissionData?.submitted && (
            <SubmissionViewer
              submissionId={submission_id}
              submissionData={submissionData}
              examPaper={examPaper}
              activeSubQuestionId={activeSubQuestionId}
              pinnedSubQuestionId={pinnedSubQuestionId}
              currentScale={currentScale}
              pdfViewKey={pdfViewKey}
              initialPage={pinnedSubQuestionId ? currentPageRef.current : 1}
              apiUrl={apiUrl}
              getQuestionIdentifier={getQuestionIdentifier}
              onScaleChange={scale => {
                setCurrentScale(scale);
                localStorage.setItem('currentScale', scale.toString());
              }}
              onPageChange={page => {
                setCurrentPdfPage(page);
                currentPageRef.current = page;
                // Only save the page number when in pinned mode
                if (pinnedSubQuestionId) {
                  localStorage.setItem(`pinnedPage_${pinnedSubQuestionId}`, page.toString());
                }
              }}
            />
          )}
        </div>

        {/* Grading and feedback area */}
        <div
          ref={rightPanelRef}
          className="w-1/2 p-6 overflow-y-auto"
          style={{ height: '100%', width: '50%', flexBasis: '50%' }}
        >
          {/* Student assignment section */}
          {assignment?.is_exam && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned to:</label>
              <div className="flex w-full space-x-2">
                <div className="flex-grow">
                  <StudentSearchDropdown
                    search={search}
                    isLoading={isAssignmentStudentsLoading}
                    pageNumber={Number(scrollPageNumber)}
                    itemsPerPage={itemsPerPage}
                    students={students}
                    currentStudentName={studentName}
                    updateSearchParams={updateSearchParams}
                    onStudentSelect={(student, hasSubmission) => {
                      setNewStudentAssignment(student);

                      if (hasSubmission) {
                        // If they have a submission, show confirmation dialog
                        setIsStudentAssignmentConfirmationOpen(true);
                      } else {
                        // If they don't have a submission, assign directly without confirmation
                        (async () => {
                          try {
                            await gradeApi.post({
                              path: `/api/assignment/${assignment_id}/submissions/${submission_id}/assign-student/`,
                              body: { new_student_id: student.id },
                              config: {
                                headers: {
                                  Authorization: `Bearer ${user.session_token}`
                                }
                              }
                            });

                            setAssignedStudentId(student.id);
                            setStudentName(student.full_name);
                            getAssignmentSubmission();
                          } catch (error) {
                            console.error('Failed to assign student:', error);
                            setError('Failed to assign student. Please try again.');
                          }
                        })();
                      }
                    }}
                  />
                </div>
                <button
                  className="h-10 px-3 py-2 bg-red-50 hover:bg-red-100 border border-red-200 rounded flex items-center text-red-600 self-end transition-colors"
                  title="Delete all submissions for this student"
                  onClick={handleDeleteSubmission}
                >
                  <TrashIcon className="h-5 w-5 mr-1.5" />
                  <span className="text-sm font-medium">Delete</span>
                </button>
              </div>
              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            </div>
          )}

          {/* Grading Rubric Component */}
          <GradingRubric
            examPaper={examPaper}
            parentQuestion={null} // Add parentQuestion prop (null as default, will use examPaper.questions fallback)
            updateExamPaper={setExamPaper} // Add function to update examPaper state
            uploadQuestions={getQuestions} // Use getQuestions as uploadQuestions to save changes
            regradeInProgress={regradeInProgress}
            pinnedSubQuestionId={pinnedSubQuestionId}
            togglePinSubQuestion={togglePinSubQuestion}
            handleRubricItemChange={handleRubricItemChange}
            getFeedback={getFeedback}
            getInstructorFeedback={getInstructorFeedback}
            getRR={getRR}
            getRRText={getRRText}
            isEditing={isEditing}
            isEditingInstructor={isEditingInstructor}
            isEditingFeedback={isEditingFeedbackCheck}
            handleRemoveFeedback={handleRemoveFeedback}
            handleAddInstructorFeedback={handleAddInstructorFeedback}
            handleInstructorCommentChange={handleInstructorCommentChange}
            handleInstructorCommentSave={handleInstructorCommentSave}
            handleInstructorCommentCancel={handleInstructorCommentCancel}
            handleRegradeReq={handleRegradeReq}
            handleEditFeedback={handleEditFeedback}
            handleFeedbackChange={handleFeedbackChange}
            handleFeedbackSave={handleFeedbackSave}
            handleFeedbackCancel={handleFeedbackCancel}
            editableFeedback={editableFeedback}
            instructorCommentRefs={instructorCommentRefs}
            editableInstructorComment={editableInstructorComment}
            regradeRef={regradeRef}
            handleRegradeClick={handleRegradeClick}
            props={props}
            assignment_id={assignment_id}
            user={user}
            setMessage={setMessage}
            getQuestions={getQuestions}
            getAssignmentSubmission={getAssignmentSubmission}
            sortedSubmissionIds={sortedSubmissionIds} // Add this prop
            submission_id={submission_id} // Add this prop
          />
        </div>
      </div>

      {console.log(
        'Debugging examPaper subquestions:',
        examPaper.flatMap(question => question.subQuestions)
      )}

      {isConfirmationOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Confirm AI-Regrade</h2>
            <p className="mb-6 text-gray-700">
              Are you sure you want to regrade this subquestion? This will affect all student
              submissions and may overwrite previous manual grades.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                className="back-btn"
                style={{
                  background: 'white',
                  color: '#4b5563',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 2,
                  fontWeight: 500
                }}
                onClick={() => setIsConfirmationOpen(false)}
                onMouseOver={e => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                Cancel
              </button>
              <button
                className="proceed-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1.25rem',
                  background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: 500,
                  border: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)',
                  zIndex: 2
                }}
                onClick={handleConfirmRegrade}
                onMouseOver={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(228, 177, 122, 0.3)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(228, 177, 122, 0.2)';
                }}
              >
                Confirm Regrade
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">Delete Student Submissions</h2>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={handleCloseDeleteModal}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="my-4">
              <div className="flex items-center justify-center mb-4 text-red-500">
                <div className="bg-red-100 p-3 rounded-full">
                  <TrashIcon className="h-8 w-8" />
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                <p className="text-red-700">
                  <strong>Warning:</strong> This action cannot be undone.
                </p>
                <p className="text-red-700 mt-2">
                  This will permanently delete <strong>all submissions</strong> for this student
                  from this assignment, including all grades, comments, and associated data.
                </p>
              </div>
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete all submissions for this student?
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                className="back-btn"
                style={{
                  background: 'white',
                  color: '#4b5563',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 2,
                  fontWeight: 500
                }}
                onClick={handleCloseDeleteModal}
                onMouseOver={e => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                Cancel
              </button>
              <button
                className="proceed-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1.25rem',
                  background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: 500,
                  border: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)',
                  zIndex: 2
                }}
                onClick={confirmDeleteSubmission}
                onMouseOver={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(228, 177, 122, 0.3)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(228, 177, 122, 0.2)';
                }}
              >
                Delete All
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Loading indicator */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <p>Regrading in progress...</p>
            <p>Expected wait time: 30 seconds</p>
          </div>
        </div>
      )}
      {/* Student Assignment Confirmation Modal */}
      {isStudentAssignmentConfirmationOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Confirm Student Assignment</h2>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setIsStudentAssignmentConfirmationOpen(false)}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="my-4">
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
                <p className="text-amber-700">
                  <strong>Warning:</strong> This student already has a submission for this
                  assignment.
                </p>
                <p className="text-amber-700 mt-2">
                  Assigning this unmatched submission to{' '}
                  {newStudentAssignment?.full_name || 'this student'} will replace their existing
                  submission with this one.
                </p>
              </div>
              <p className="text-gray-600 mb-4">Are you sure you want to proceed?</p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                className="back-btn"
                style={{
                  background: 'white',
                  color: '#4b5563',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  zIndex: 2,
                  fontWeight: 500
                }}
                onClick={() => setIsStudentAssignmentConfirmationOpen(false)}
                onMouseOver={e => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                Cancel
              </button>
              <button
                className="proceed-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.75rem 1.25rem',
                  background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                  color: 'white',
                  borderRadius: '0.5rem',
                  fontWeight: 500,
                  border: 'none',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)',
                  zIndex: 2
                }}
                onClick={handleConfirmStudentAssignment}
                onMouseOver={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(228, 177, 122, 0.3)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(228, 177, 122, 0.2)';
                }}
              >
                Confirm Assignment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// This component preloads PDFs using direct fetch requests
const PdfPreloader = React.memo(({ pdfFiles, apiUrl, singleFile }) => {
  // If we have a single file to preload, use that
  const fileToUse = singleFile || null;

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const doFetchPrefetch = async filename => {
      if (!filename) {
        return;
      }
      const url = `${apiUrl}/api/pdf-file/${filename}?mode=view`;
      console.log(`⏳ Pre-fetching PDF via fetch: ${filename}`);
      try {
        // Make the fetch request. We don't need the body, just the act of fetching.
        // The browser should cache based on response headers.
        const response = await fetch(url, { signal });
        if (response.ok) {
          // Optional: Could check response headers or status if needed
          console.log(`✅ Successfully pre-fetched PDF: ${filename} (Status: ${response.status})`);
        } else {
          console.error(`❌ Failed to pre-fetch PDF: ${filename} (Status: ${response.status})`);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log(`⏹️ Pre-fetch aborted for: ${filename}`);
        } else {
          console.error(`❌ Error during pre-fetch for ${filename}:`, error);
        }
      }
    };

    // Handle the case of a single file specified through the singleFile prop
    if (singleFile) {
      doFetchPrefetch(singleFile);
    }

    // Handle the case of multiple files
    if (pdfFiles && pdfFiles.length > 0) {
      console.log(`🔄 PdfPreloader is pre-fetching ${pdfFiles.length} PDF files via fetch`);
      pdfFiles.forEach(pdf => {
        doFetchPrefetch(pdf);
      });
    }

    return () => {
      // Abort any ongoing fetches when the component unmounts or dependencies change
      console.log('🧹 Aborting pre-fetches...');
      controller.abort();
    };
  }, [pdfFiles, singleFile, apiUrl]);
  return null; // Return null so it doesn't render anything
});

export default NewTDetails;
